import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";
import {
  db,
  clearAllLocalBusinessData,
  purgeLegacyDemoData,
} from "./db";
import { refreshFromServer } from "./data";
import { flushSyncQueue } from "./sync";

const CLEANUP_VERSION = "v3";

export interface Session {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  tenant?: {
    id: string;
    name: string;
    slug?: string | null;
  };
  store: {
    id: string;
    name: string;
    displayName?: string | null;
    businessType?: string | null;
    role?: string;
  };
  stores?: Array<{
    id: string;
    name: string;
    businessType?: string | null;
    role: string;
  }>;
}

interface AuthContextValue {
  ready: boolean;
  authenticated: boolean;
  demo: boolean;
  session: Session | null;
  login(
    email: string,
    password: string,
  ): Promise<void>;
  loginWithGoogle(credential: string): Promise<void>;
  signup(input: {
    ownerName: string;
    email: string;
    password: string;
    storeName: string;
    businessType?: string;
  }): Promise<void>;
  switchStore(storeId: string): Promise<void>;
  bootstrap(input: {
    storeName: string;
    businessType?: string;
    ownerName: string;
    email: string;
    password: string;
    bootstrapToken: string;
  }): Promise<void>;
  invalidateSession(): void;
  logout(): Promise<void>;
}

const AuthContext =
  createContext<AuthContextValue | null>(
    null,
  );

function saveSession(
  session: Session,
): void {
  localStorage.setItem(
    "bledger.session",
    JSON.stringify(session),
  );
}

function clearSessionCache(): void {
  localStorage.removeItem(
    "bledger.session",
  );
}

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(
      "bledger.session",
    );
    return raw
      ? (JSON.parse(raw) as Session)
      : null;
  } catch {
    return null;
  }
}

async function syncThenRefresh(): Promise<void> {
  if (!navigator.onLine) return;
  await flushSyncQueue();
  await refreshFromServer();
}

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [ready, setReady] =
    useState(false);
  const [session, setSession] =
    useState<Session | null>(
      readSession(),
    );
  const demo = false;

  function invalidateSession() {
    clearSessionCache();
    setSession(null);
  }

  useEffect(() => {
    void (async () => {
      try {
        if (localStorage.getItem("bledger.productionCleanupVersion") !== CLEANUP_VERSION) {
          await purgeLegacyDemoData();
          localStorage.removeItem("bledger.demo");

          const cachedSession = readSession();
          if (
            cachedSession?.user?.id?.startsWith("demo-") ||
            cachedSession?.store?.id?.startsWith("demo-")
          ) {
            clearSessionCache();
            setSession(null);
          }

          localStorage.setItem("bledger.productionCleanupVersion", CLEANUP_VERSION);
        }

        const cached = readSession();
        if (
          navigator.onLine &&
          cached
        ) {
          const current =
            await api<Session>(
              "/auth/me",
            );

          setSession(current);
          saveSession(current);

          await syncThenRefresh();
        }
      } catch (error) {
        if (
          (error as {
            status?: number;
          }).status === 401
        ) {
          invalidateSession();
        }
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const value =
    useMemo<AuthContextValue>(
      () => ({
        ready,
        authenticated:
          demo || Boolean(session),
        demo,
        session,

        async login(email, password) {
          await clearAllLocalBusinessData();

          const result = await api<{
            user: Session["user"];
            store: Session["store"];
            tenant: Session["tenant"];
          }>("/auth/login", {
            method: "POST",
            body: JSON.stringify({
              email,
              password,
            }),
          });

          localStorage.removeItem(
            "bledger.demo",
          );

          const me = await api<Session>("/auth/me").catch(() => ({
            user: result.user,
            store: result.store,
            tenant: result.tenant,
          }));

          saveSession(me);
          setSession(me);

          await syncThenRefresh();
        },

        async loginWithGoogle(credential: string) {
          await clearAllLocalBusinessData();

          const result = await api<{
            user: Session["user"];
            store: Session["store"];
            tenant: Session["tenant"];
          }>("/auth/google", {
            method: "POST",
            body: JSON.stringify({ credential }),
          });

          localStorage.removeItem("bledger.demo");

          const me = await api<Session>("/auth/me").catch(() => ({
            user: result.user,
            store: result.store,
            tenant: result.tenant,
          }));

          saveSession(me);
          setSession(me);

          await syncThenRefresh();
        },

        async signup(input) {
          await clearAllLocalBusinessData();

          const result = await api<{
            user: Session["user"];
            store: Session["store"];
            tenant: Session["tenant"];
          }>("/auth/signup", {
            method: "POST",
            body: JSON.stringify(input),
          });

          localStorage.removeItem(
            "bledger.demo",
          );

          const me = await api<Session>("/auth/me").catch(() => ({
            user: result.user,
            store: result.store,
            tenant: result.tenant,
          }));

          saveSession(me);
          setSession(me);

          await refreshFromServer();
        },

        async switchStore(storeId: string) {
          await api<{ store: Session["store"] }>("/stores/switch", {
            method: "POST",
            body: JSON.stringify({ storeId }),
          });

          // Strict offline data isolation: clear prior store cache!
          await clearAllLocalBusinessData();

          const me = await api<Session>("/auth/me");
          saveSession(me);
          setSession(me);

          await refreshFromServer();
        },

        async bootstrap(input) {
          await clearAllLocalBusinessData();

          const result = await api<{
            user: Session["user"];
            store: Session["store"];
          }>("/auth/bootstrap", {
            method: "POST",
            headers: {
              "x-bootstrap-token":
                input.bootstrapToken,
            },
            body: JSON.stringify({
              storeName: input.storeName,
              businessType:
                input.businessType,
              ownerName: input.ownerName,
              email: input.email,
              password: input.password,
            }),
          });

          localStorage.removeItem(
            "bledger.demo",
          );

          const me = await api<Session>("/auth/me").catch(() => ({
            user: { ...result.user, email: input.email },
            store: result.store,
          }));

          saveSession(me);
          setSession(me);

          await refreshFromServer();
        },

        invalidateSession,

        async logout() {
          if (
            !demo &&
            navigator.onLine
          ) {
            try {
              await api<void>(
                "/auth/logout",
                {
                  method: "POST",
                },
              );
            } catch {
              // Local sign-out still removes access in this client.
            }
          }

          clearSessionCache();
          localStorage.removeItem(
            "bledger.demo",
          );

          await clearAllLocalBusinessData();
          await db.delete().catch(() => {});

          setSession(null);
        },
      }),
      [ready, session],
    );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error(
      "useAuth must be used inside AuthProvider",
    );
  }

  return value;
}
